import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Anchor, ShieldAlert, ChevronDown, ChevronUp, Flag } from "lucide-react";
import { API_URL } from "../config/api";

const PLATFORM_API = `${API_URL}/v2/super-admin`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` } });
const pinHeaders = (_pin, token) => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}`, 'X-Stepup-Token': token, 'X-Admin-Otp': token },
});

/**
 * Ask Bosun controls inside the Super Admin org Edit dialog:
 * status + strikes, enable/disable toggle (PIN-gated), and the full
 * conversation log. Auto-disabled orgs show a locked state that only a
 * BookingFlows admin can lift here.
 */
export const BosunControlCard = ({ org, requirePin }) => {
  const [status, setStatus] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [messages, setMessages] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${PLATFORM_API}/organizations/${org.id}/bosun`, authHeaders());
      setStatus(res.data);
    } catch (e) { /* card is best-effort */ }
  };

  useEffect(() => { load(); }, [org.id]); // eslint-disable-line

  const toggle = () => {
    const enabling = !status?.enabled;
    const label = enabling
      ? (status?.autoDisabled
        ? `RE-ENABLE Ask Bosun for "${org.name}" — this clears the auto-disable lock and strike count (BookingFlows review)`
        : `Enable Ask Bosun for "${org.name}"`)
      : `Disable Ask Bosun for "${org.name}"`;
    requirePin(label, async (pin, otp) => {
      await axios.post(`${PLATFORM_API}/organizations/${org.id}/bosun/toggle`, { enabled: enabling }, pinHeaders(pin, otp));
      await load();
    });
  };

  const openLog = async () => {
    const next = !logOpen;
    setLogOpen(next);
    if (next && messages === null) {
      try {
        const res = await axios.get(`${PLATFORM_API}/organizations/${org.id}/bosun/messages?limit=100`, authHeaders());
        setMessages(res.data.messages || []);
      } catch (e) {
        setMessages([]);
      }
    }
  };

  if (!status) return null;

  return (
    <div className="pt-4 border-t border-gray-200 space-y-3" data-testid="bosun-control-card">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <Anchor className="h-4 w-4 text-[#1F2A5C]" />
        Ask Bosun
      </h3>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          {status.enabled ? (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700" data-testid="bosun-status-badge">On watch</span>
          ) : status.autoDisabled ? (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 flex items-center gap-1" data-testid="bosun-status-badge">
              <ShieldAlert className="h-3 w-3" /> LOCKED — auto-disabled
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-700" data-testid="bosun-status-badge">Off duty</span>
          )}
          <span className="text-xs text-gray-500">
            {status.totalMessages} msgs · {status.flaggedMessages} flagged · {status.strikeCount} strike{status.strikeCount === 1 ? '' : 's'}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={toggle}
          data-testid="bosun-toggle-button"
          className={status.enabled ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-green-700 border-green-200 hover:bg-green-50'}
        >
          {status.enabled ? 'Disable Bosun' : status.autoDisabled ? 'Review & Re-enable' : 'Enable Bosun'}
        </Button>
      </div>

      {status.autoDisabled && status.disabledReason && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="bosun-disabled-reason">
          {status.disabledReason} — requires BookingFlows review before re-enabling.
        </p>
      )}

      <button
        onClick={openLog}
        data-testid="bosun-log-toggle"
        className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800"
      >
        {logOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {logOpen ? 'Hide conversation log' : 'View conversation log'}
      </button>

      {logOpen && (
        <div className="max-h-64 overflow-y-auto space-y-1.5 border border-gray-200 rounded-lg p-2 bg-gray-50" data-testid="bosun-message-log">
          {messages === null ? (
            <p className="text-xs text-gray-400 p-2">Loading…</p>
          ) : messages.length === 0 ? (
            <p className="text-xs text-gray-400 p-2">No Bosun messages yet for this organization.</p>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`rounded-lg px-2.5 py-1.5 text-xs ${m.flagged ? 'bg-red-50 border border-red-200' : m.role === 'user' ? 'bg-white border border-gray-200' : 'bg-blue-50/60'}`}>
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="font-semibold text-gray-700">
                    {m.role === 'user' ? (m.userEmail || 'operator') : 'Bosun'}
                    {m.flagged && <Flag className="inline h-3 w-3 text-red-500 ml-1" />}
                  </span>
                  <span className="text-[10px] text-gray-400">{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</span>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap break-words">{m.content}</p>
                {m.flagged && m.flagReason && (
                  <p className="text-[10px] text-red-600 mt-0.5">⚑ {m.flagReason}</p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default BosunControlCard;
